import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { first, filter, lowerCase } from 'lodash';
import { Tab } from 'react-bootstrap';

import Delightable from '../../../../components/delightable';
import StageDisplay from './Stage';
import DatetimeDisplay from '../../../../components/date-time-display';

import styles from './styles.css';

class FeedbackTab extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.showInsight = this.showInsight.bind(this);
  }

  componentDidMount() {
    this.props.measure();
  }

  getRawStage(feedbacks) {
    const { object } = this.props;

    const clientFeedback = first(feedbacks);
    let { stage } = object;

    if (clientFeedback) {
      if (clientFeedback.outcome === 1) {
        stage = 'Won';
      } else if (clientFeedback.outcome === 2) {
        stage = 'Lost';
      } else if (clientFeedback.outcome === 3) {
        stage = 'No Decision';
      } else if (clientFeedback.outcome === 5) {
        stage = 'Pending';
      }
    }
    return stage;
  }

  getStage(feedbacks) {
    const stage = this.getRawStage(feedbacks);
    const isWon = stage === 'Won';
    const comp = (<StageDisplay value={stage} type="verb" />);
    if (isWon) {
      return (
        <Delightable>{comp}</Delightable>
      );
    }
    return comp;
  }

  showInsight(feedback) {
    this.props.requestShowInsight(feedback.insight_id);
  }

  renderHTML(raw) {
    return <span dangerouslySetInnerHTML={{ __html: raw }} />;
  }

  renderTokens(feedbacks) {
    const total = feedbacks.length;
    return feedbacks.map((feedback, i) => {
      let suffix = null;
      let prefix = null;
      if (i < total - 2) {
        suffix = (<span>, </span>);
      } else if (total > 1 && i === total - 1) {
        prefix = (<span className={styles.ampersand}> &amp; </span>);
      }

      /*
      const title = `${feedback.label}: ${feedback.impact_text} ${feedback.classification_text}`;
      const popover = (
        <Tooltip id={`feedback-${feedback.id}`} title={title}>
          <div className="text-left">
            <div><strong>{title}</strong></div>
            <div>{this.renderHTML(feedback.insight_statement)}</div>
          </div>
        </Tooltip>
      );
      return (
        <span key={`${feedback.id}-${feedback.contact_id}-${feedback.insight_id}`}>
          {prefix}
          <OverlayTrigger key={feedback.id} trigger={['hover', 'focus']} placement="top" overlay={popover}>
            <a style={{ display: 'inline-block' }} href="" onClick={(e) => { e.preventDefault(); this.showInsight(feedback); }}>{feedback.label}</a>
          </OverlayTrigger>
          {suffix}
        </span>
      );
      */
      return (
        <span key={`${feedback.id}-${feedback.contact_id}-${feedback.insight_id}`}>
          {prefix}
          <a style={{ display: 'inline-block' }} href="" onClick={(e) => { e.preventDefault(); this.showInsight(feedback); }}>{feedback.label}</a>
          {suffix}
        </span>
      );
    });
  }

  renderClassifications(feedbacks, isMajor = false) {
    const f = first(feedbacks);
    if (!f) {
      return null;
    }
    const classification = f.classification_text; // Advantage/Disadvantage
    return [
      (<span key="classification" className="text-lowercase">{isMajor ? 'major' : 'minor'} {classification} in </span>),
      (<span key="tokens" className="text-lowercase">{this.renderTokens(feedbacks)}</span>),
      (<span key="period">.</span>),
    ];
  }

  renderReasons(feedbacks) {
    // all from 1 contact
    const rawStage = this.getRawStage(feedbacks);
    const stage = this.getStage(feedbacks);
    const majors = filter(feedbacks, { impact: 1 });
    const minors = filter(feedbacks, { impact: 2 });

    const majorSentence = this.renderClassifications(majors, true);
    const minorSentence = this.renderClassifications(minors);
    if (!majorSentence && !minorSentence) {
      return null;
    }
    const firstPart = majorSentence || minorSentence;
    const secondPart = majorSentence ? minorSentence : null;
    const join = secondPart ? 'You also had a' : null;
    const action = rawStage === 'Pending' ? '' : (<span>{stage} because you</span>);
    const br = secondPart ? <br /> : null;
    return (
      <span>You {action} had a {firstPart} {br} {join} {secondPart}</span>
    );
  }

  renderAccountClassifications(feedbacks, isMajor = false) {
    const f = first(feedbacks);
    if (!f) {
      return null;
    }
    const prefix = isMajor ? 'Customer experience is' : 'Their experience has been';
    const isPossitive = f.classification_text === 'Positive';
    const cls = isPossitive ? 'text-success' : 'text-danger';
    const pos = isPossitive ? 'positive' : 'negative';
    let classification = isMajor ? `very ${pos}` : `${pos}ly influenced`;
    if (isMajor && isPossitive) {
      classification = (<Delightable>{classification}</Delightable>);
    }
    return [
      (<span key="classification">{prefix} <span className={cls}>{classification}</span> due to </span>),
      (<span key="tokens" className="text-lowercase">{this.renderTokens(feedbacks)}</span>),
      (<span key="period">.</span>),
    ];
  }

  renderAccountReasons(feedbacks) {
    // all from 1 contact
    const majors = filter(feedbacks, { impact: 1 });
    const minors = filter(feedbacks, { impact: 2 });

    const majorSentence = this.renderAccountClassifications(majors, true);
    const minorSentence = this.renderAccountClassifications(minors);
    if (!majorSentence && !minorSentence) {
      return null;
    }
    const firstPart = majorSentence || minorSentence;
    const secondPart = majorSentence ? minorSentence : null;
    const join = secondPart ? ' ' : null;
    return (
      <span>{firstPart} {join} {secondPart}</span>
    );
  }

  renderPricePosition(contact) {
    if (!contact.price_position) {
      return null;
    }
    const value = contact.price_position.label;
    if (value === 'NA') {
      return null;
    }
    const { object } = this.props;
    // const isWon = object.stage === 'Won';
    if (!object.strongest_competitors || object.strongest_competitors.length === 0) {
      return null;
    }
    const lowerCaseValue = lowerCase(value);

    const cmp = lowerCaseValue === 'similar' ? 'to' : 'than';

    // const cls = isWon ? 'text-success' : 'text-danger';
    // not base on outcome stage
    const cls = lowerCaseValue === 'higher' ? 'text-danger' : 'text-success';

    let vendorComp = contact.price_position.vendor_name;
    if (contact.price_position.battlecard_url) {
      vendorComp = (<a href={contact.price_position.battlecard_url} target="_blank">{vendorComp}</a>);
    }
    return [
      (<br key="br" />),
      (<span key="s">Your price position was <span className={`text-lowercase ${cls}`}>{value}</span> {cmp} {vendorComp}.</span>),
    ];
  }

  renderNPS(contact) {
    const nps = contact.nps_type;
    const lbl = nps;
    let cls = 'label label-default';
    if (nps === 'Promoter') {
      cls = 'label label-success';
    } else if (nps === 'Detractor') {
      cls = 'label label-danger';
    }
    if (lbl) {
      return (<span>&nbsp;<span className={cls}>{lbl}</span></span>);
    }
    return null;
  }

  renderDate(feedbacks) {
    const f = first(feedbacks);
    if (!f || !f.publish_date) {
      return null;
    }
    return (<div className="text-muted"><small>Provided feedback: <DatetimeDisplay value={f.publish_date} relative /></small></div>);
  }

  renderFeedback(feedbacks) {
    // all from 1 contact
    const { isAccount, includeDate } = this.props;
    const contact = first(feedbacks);
    if (!contact) {
      return null;
    }
    const reasons = isAccount ? this.renderAccountReasons(feedbacks) : this.renderReasons(feedbacks);
    const pricePosition = isAccount ? null : this.renderPricePosition(contact);
    const nps = isAccount ? this.renderNPS(contact) : null;

    const date = includeDate ? this.renderDate(feedbacks) : null;

    let contactComp = (<strong>{contact.contact_name}</strong>);
    if (contact.transcript_url) {
      contactComp = (<a href={contact.transcript_url} target="_blank">{contactComp}</a>);
    }

    let publishDateComp = null;
    if (contact.has_no_impacts && contact.publish_date) {
      publishDateComp = (
        <span>Provided feedback on <DatetimeDisplay value={contact.publish_date} />.</span>
      );
    }

    return (
      <div className="well" key={contact.contact_id}>
        <div>{contactComp} {nps}: {publishDateComp} {reasons} {pricePosition}</div>
        {date}
      </div>
    );
  }

  renderList() {
    const { feedbacks } = this.props;
    // array of array
    return (
      <div className={styles.section}>
        {feedbacks.map(feedback => this.renderFeedback(feedback))}
      </div>
    );
  }

  renderEmpty() {
    const { isPast, isAccount } = this.props;
    let msg = 'No contacts have provided feedback yet.';
    if (isPast && isAccount) {
      msg = 'No past feedback found.';
    }
    return (
      <div className={`${styles.section} text-left`}>
        <div className="well well-sm">
          {msg}
        </div>
      </div>
    );
  }

  render() {
    const {
      eventKey, title, totalFeedbacks,
      isTab,
    } = this.props;
    const content = (
      <div>
        {totalFeedbacks === 0 && this.renderEmpty()}
        {totalFeedbacks > 0 && this.renderList()}
      </div>
    );
    if (!isTab) {
      return content;
    }
    return (
      <Tab
        eventKey={eventKey}
        title={title}
      >
        {content}
      </Tab>
    );
  }
}

FeedbackTab.propTypes = {
  object: PropTypes.shape({
    opportunity_id: PropTypes.string,
    account_id: PropTypes.string,
  }).isRequired,
  isTab: PropTypes.bool,
  eventKey: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
  totalFeedbacks: PropTypes.number,
  feedbacks: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))).isRequired, // array of arrays
  requestShowInsight: PropTypes.func,
  isAccount: PropTypes.bool,
  includeDate: PropTypes.bool,
  isPast: PropTypes.bool,
  measure: PropTypes.func,
};

FeedbackTab.defaultProps = {
  totalFeedbacks: 0,
  requestShowInsight: () => {},
  isAccount: false,
  includeDate: false,
  isPast: false,
  isTab: true,
  measure: () => {},
};

export default FeedbackTab;
